import {
  ArrowLeftOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  FilterOutlined,
  FileTextOutlined,
  GithubOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Button, Input } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { consumerSalesAppId, defaultCommitMessage, demoApps } from './demoData';
import { clearPreviewReview, readPreviewReview, readPreviewSession, savePreviewReview, savePreviewSession } from './previewSession';
import { VisualDiffCard } from './VisualDiffCard';
import { CapabilityVisualDiff, CapabilityVisualPair } from './CapabilityVisualDiff';
import { AiChangeInterpretation, buildVisualAnnotations } from './AiChangeInterpretation';
import { ReviewExecutionRoadmap } from './ReviewExecutionRoadmap';
import { AppSectionTabs } from './AppSectionTabs';
import { saveSyncSummary } from './syncSummary';
import { clickCiApiUrl } from './apiUrl';

const sampleChanges = [
  ['Revenue by Region', 'Bar chart', 'Modified', 'High'],
  ['Sales Trend', 'Line chart', 'Modified', 'Medium'],
  ['Total Revenue KPI', 'KPI', 'Modified', 'Low'],
  ['Gross Margin %', 'Master measure', 'Modified', 'High'],
  ['Avg Order Value', 'Master measure', 'Added', 'Medium'],
  ['Sales Overview', 'Sheet', 'Modified', 'Medium'],
  ['Regional Analysis', 'Sheet', 'Added', 'Low'],
  ['Load script', 'Data load editor', 'Modified', 'High'],
  ['Variable: vTargetMargin', 'Variable', 'Removed', 'Low'],
];

interface LiveChange {
  folder: string;
  objectId: string;
  objectType: string;
  title?: string;
  changeType: 'added' | 'modified' | 'removed';
  risk: 'low' | 'medium' | 'high';
  businessSummary: string;
}

interface LiveReview {
  baselineCommit: string;
  exportedFiles: number;
  reviewToken: string;
  reviewedContentDigest: string;
  review: {
    changes: LiveChange[];
    summary: string;
    riskLevel: string;
    recommendation: string;
  };
  incomingReview: {
    changes: LiveChange[];
    summary: string;
    riskLevel: string;
    recommendation: string;
  };
  visualPairs?: CapabilityVisualPair[];
  incomingVisualPairs?: CapabilityVisualPair[];
  syncPlan?: {
    baseCommit: string;
    gitHeadCommit: string;
    status: 'in_sync' | 'qlik_ahead' | 'git_ahead' | 'auto_merge' | 'conflict';
    gitChanges: Array<{ folder: string; objectId: string; title?: string }>;
    qlikChanges: Array<{ folder: string; objectId: string; title?: string }>;
    identicalChanges: Array<{ folder: string; objectId: string; title?: string }>;
    conflicts: Array<{ folder: string; objectId: string; title?: string; reason: 'both_modified' | 'delete_vs_edit' }>;
  };
}

const apiUrl = clickCiApiUrl;

type ChangeCategory = 'business_logic' | 'visual' | 'label' | 'layout' | 'other';

const riskRank = { high: 3, medium: 2, low: 1 } as const;

const categoryLabels: Record<ChangeCategory, string> = {
  business_logic: 'Business logic',
  visual: 'Visual changes',
  label: 'Labels and text',
  layout: 'Layout and sheets',
  other: 'Other changes',
};

const classifyChange = (change: LiveChange): ChangeCategory => {
  const type = change.objectType.toLowerCase();
  const summary = change.businessSummary.toLowerCase();
  if (/measure|dimension|expression|script|variable/.test(type) || /measure|dimension|expression|script|filter|selection/.test(summary)) return 'business_logic';
  if (/chart|kpi|line|bar|pie|scatter|table|visual/.test(type) || /visual|color|axis|legend|chart/.test(summary)) return 'visual';
  if (/sheet|layout|container/.test(type) || /layout|sheet/.test(summary)) return 'layout';
  if (/title|label|text|description/.test(summary)) return 'label';
  return 'other';
};

const requiresIndividualReview = (change: LiveChange) => change.risk !== 'low';

const objectIconFor = (change: LiveChange) => {
  const type = change.objectType.toLowerCase();
  if (/bar|line|pie|chart|kpi/.test(type)) return <BarChartOutlined />;
  if (/sheet|layout/.test(type)) return <AppstoreOutlined />;
  return <FileTextOutlined />;
};

const businessImpactLabel = (change: LiveChange) => {
  const category = classifyChange(change);
  if (category === 'business_logic') return 'Business rule';
  if (category === 'visual') return 'Visual impact';
  if (category === 'label') return 'Label change';
  if (category === 'layout') return 'Layout change';
  return 'Content change';
};

export const ChangeReviewPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const location = useLocation();
  const app = demoApps.find(item => item.id === appId) || demoApps[0];
  const queryPreviewId = useMemo(() => new URLSearchParams(location.search).get('previewAppId') ?? undefined, [location.search]);
  const preview = useMemo(() => {
    const savedPreview = readPreviewSession(app.id);
    return queryPreviewId && savedPreview?.previewAppId !== queryPreviewId
      ? {
        sourceAppId: app.id,
        previewAppId: queryPreviewId,
        previewAppName: 'Click-CI Preview (linked)',
        createdAt: new Date().toISOString(),
      }
      : savedPreview;
  }, [app.id, queryPreviewId]);
  const previewAppId = queryPreviewId ?? preview?.previewAppId;
  const reviewTargetId = previewAppId ?? app.id;
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [liveReview, setLiveReview] = useState<LiveReview>();
  const [loading, setLoading] = useState(app.id === consumerSalesAppId);
  const [loadError, setLoadError] = useState<string>();
  const [reviewedObjectIds, setReviewedObjectIds] = useState<Set<string>>(new Set());
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [commitMessage, setCommitMessage] = useState(defaultCommitMessage);
  const [checkingIn, setCheckingIn] = useState(false);
  const [commitResult, setCommitResult] = useState<{ type: 'success' | 'error'; message: string }>();

  const isIncomingGit = false;
  const activeReview = isIncomingGit ? liveReview?.incomingReview : liveReview?.review;
  const activeVisualPairs = isIncomingGit ? liveReview?.incomingVisualPairs : liveReview?.visualPairs;
  const selectedLiveChange = activeReview?.changes[Math.min(selectedIndex, Math.max(0, activeReview.changes.length - 1))];
  const selectedVisualPair = selectedLiveChange
    ? (activeVisualPairs ?? []).find(pair => pair.afterObjectId === selectedLiveChange.objectId || pair.beforeObjectId === selectedLiveChange.objectId)
    : undefined;
  const visualAnnotations = useMemo(() => buildVisualAnnotations(selectedVisualPair), [selectedVisualPair]);

  useEffect(() => {
    if (preview && preview.previewAppId === queryPreviewId) {
      savePreviewSession(preview);
    }
  }, [preview, queryPreviewId]);

  useEffect(() => {
    if (app.id !== consumerSalesAppId || typeof fetch !== 'function') return;
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 90_000);
    setLoading(true); setLoadError(undefined);
    const previewQuery = previewAppId ? `?previewAppId=${encodeURIComponent(previewAppId)}` : '';
    void fetch(`${apiUrl}/api/demo-ci/apps/${app.id}/changes${previewQuery}`, { signal: controller.signal })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.message ?? 'Change review failed');
        if (active && payload?.result?.review?.changes) {
          const result = payload.result as LiveReview;
          setLiveReview(result);
          saveSyncSummary({
            appId: app.id,
            status: result.syncPlan?.status ?? (result.review.changes.length ? 'qlik_ahead' : 'in_sync'),
            baselineCommit: result.syncPlan?.gitHeadCommit ?? result.baselineCommit,
            qlikChangeCount: result.syncPlan?.qlikChanges.length ?? result.review.changes.length,
            gitChangeCount: result.syncPlan?.gitChanges.length ?? 0,
            conflictCount: result.syncPlan?.conflicts.length ?? 0,
            comparedAt: new Date().toISOString(),
          });
          const saved = readPreviewReview(app.id);
          const matchingSaved = saved?.previewAppId === reviewTargetId && saved.reviewToken === result.reviewToken
            ? saved
            : undefined;
          const reviewedIds = matchingSaved?.reviewedObjectIds ?? [];
          setReviewedObjectIds(new Set(reviewedIds));
          const hasConflicts = (result.syncPlan?.conflicts.length ?? 0) > 0;
          savePreviewReview({
            sourceAppId: app.id,
            previewAppId: reviewTargetId,
            reviewToken: result.reviewToken,
            reviewedObjectIds: reviewedIds,
            syncStatus: result.syncPlan?.status,
            gitHeadCommit: result.syncPlan?.gitHeadCommit,
            gitChangeCount: result.syncPlan?.gitChanges.length,
            qlikChangeCount: result.syncPlan?.qlikChanges.length,
            conflictCount: result.syncPlan?.conflicts.length,
            ...(!hasConflicts && result.review.changes.length === 0
              ? { completedAt: new Date().toISOString() }
              : !hasConflicts && matchingSaved?.completedAt
                ? { completedAt: matchingSaved.completedAt }
                : {}),
          });
        }
      })
      .catch(error => {
        if (!active) return;
        setLoadError(error instanceof DOMException && error.name === 'AbortError'
          ? 'Comparison exceeded 90 seconds. The cached App status remains available; retry when Qlik is responsive.'
          : error instanceof Error ? error.message : 'Change review failed');
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [app.id, previewAppId, reviewTargetId]);

  if (loading) {
    return <section className="changes-page">
      <ReviewBreadcrumb appId={app.id} appName={app.name} />
      <ReviewBanner app={app} state="Review running" />
      <AppSectionTabs appId={app.id} active="commit" previewAppId={previewAppId} />
      <div className="change-layout is-roadmap-layout">
        <ReviewExecutionRoadmap loading mode="commit" targetLabel={previewAppId ? 'Preview App' : 'current Qlik App'} />
        <section className="review-loading-panel">
          <LoadingOutlined spin />
          <div><h2>Building canonical review evidence</h2><p>Click-CI is exporting the controlled Qlik target, normalizing stable properties, and comparing them with Git. The roadmap updates only when the server confirms the result.</p><strong>{previewAppId ? 'Reading the active Preview App' : 'Reading the current Qlik App'}</strong></div>
        </section>
      </div>
    </section>;
  }

  if (liveReview && activeReview?.changes.length === 0) {
    return (
      <section className="changes-page">
        <ReviewBreadcrumb appId={app.id} appName={app.name} />
        <ReviewBanner app={app} state={isIncomingGit && previewAppId ? 'Checkout verified' : 'Up to date'} />
        <AppSectionTabs appId={app.id} active="commit" commitCount={0} previewAppId={previewAppId} />
        {isIncomingGit && previewAppId
          ? <Alert className="live-review-context" showIcon type="success" message="Checkout verification passed" description={`This Preview matches Git ${liveReview.syncPlan?.gitHeadCommit.slice(0, 7)}. The persistent Checkout User App remains unchanged at its previous revision.`} />
          : <SyncPlanAlert review={liveReview} />}
        <section className="clean-review-state">
          <CheckCircleOutlined />
          <div><h2>{isIncomingGit && previewAppId ? 'Preview matches latest Git' : 'No changes to review'}</h2><p>{previewAppId ? 'This Preview App' : 'Current Qlik App'} already matches Git. {activeReview.summary}</p></div>
          <Link to={`/apps/${app.id}`}>Back to app</Link>
        </section>
      </section>
    );
  }

  if (liveReview && activeReview && activeReview.changes.length > 0) {
    const selected = selectedLiveChange ?? activeReview.changes[0];
    const visualPair = selectedVisualPair;
    const hasConflicts = (liveReview.syncPlan?.conflicts.length ?? 0) > 0;
    const groupedChanges = activeReview.changes.reduce<Record<ChangeCategory, LiveChange[]>>((groups, change) => {
      const category = classifyChange(change);
      groups[category] = [...(groups[category] ?? []), change];
      return groups;
    }, {
      business_logic: [],
      visual: [],
      label: [],
      layout: [],
      other: [],
    });
    const orderedGroups = (Object.keys(categoryLabels) as ChangeCategory[])
      .map(category => ({ category, changes: groupedChanges[category] }))
      .filter(group => group.changes.length > 0);
    const requiredChanges = activeReview.changes.filter(requiresIndividualReview);
    const lowRiskChanges = activeReview.changes.filter(change => !requiresIndividualReview(change));
    const reviewedRequiredCount = requiredChanges.filter(change => reviewedObjectIds.has(change.objectId)).length;
    const reviewedLowRiskCount = lowRiskChanges.filter(change => reviewedObjectIds.has(change.objectId)).length;
    const visualChangeCount = activeReview.changes.filter(change => classifyChange(change) === 'visual').length;
    const businessLogicCount = activeReview.changes.filter(change => classifyChange(change) === 'business_logic').length;
    const layoutChangeCount = activeReview.changes.filter(change => classifyChange(change) === 'layout').length;
    const labelChangeCount = activeReview.changes.filter(change => classifyChange(change) === 'label').length;
    const highestRisk = activeReview.changes.reduce<LiveChange['risk']>((risk, change) => riskRank[change.risk] > riskRank[risk] ? change.risk : risk, 'low');
    const reviewComplete = !isIncomingGit && reviewedObjectIds.size === activeReview.changes.length && !hasConflicts;
    const commitChanges = async () => {
      if (app.id !== consumerSalesAppId || isIncomingGit || !reviewComplete) return;
      setCheckingIn(true); setCommitResult(undefined);
      try {
        const response = await fetch(`${apiUrl}/api/demo-ci/apps/${app.id}/check-in`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ commitMessage, reviewToken: liveReview.reviewToken, ...(previewAppId ? { previewAppId } : {}) }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.message ?? 'Commit failed');
        clearPreviewReview(app.id);
        setCommitResult({ type: 'success', message: `Committed ${payload.result.exportedFiles} JSON files to GitHub at ${payload.result.commit.slice(0, 7)}.` });
      } catch (error) {
        setCommitResult({ type: 'error', message: error instanceof Error ? error.message : 'Commit failed' });
      } finally {
        setCheckingIn(false);
      }
    };
    const markReviewed = () => {
      if (reviewedObjectIds.has(selected.objectId)) return;
      const next = new Set(reviewedObjectIds);
      next.add(selected.objectId);
      setReviewedObjectIds(next);
      savePreviewReview({
        sourceAppId: app.id,
        previewAppId: reviewTargetId,
        reviewToken: liveReview.reviewToken,
        reviewedObjectIds: Array.from(next),
        syncStatus: liveReview.syncPlan?.status,
        gitHeadCommit: liveReview.syncPlan?.gitHeadCommit,
        gitChangeCount: liveReview.syncPlan?.gitChanges.length,
        qlikChangeCount: liveReview.syncPlan?.qlikChanges.length,
        conflictCount: liveReview.syncPlan?.conflicts.length,
        ...(next.size === activeReview.changes.length && !hasConflicts ? { completedAt: new Date().toISOString() } : {}),
      });
      if (selectedIndex < activeReview.changes.length - 1) setSelectedIndex(selectedIndex + 1);
    };
    const reviewLowRiskChanges = () => {
      if (!lowRiskChanges.length) return;
      const next = new Set(reviewedObjectIds);
      lowRiskChanges.forEach(change => next.add(change.objectId));
      setReviewedObjectIds(next);
      savePreviewReview({
        sourceAppId: app.id,
        previewAppId: reviewTargetId,
        reviewToken: liveReview.reviewToken,
        reviewedObjectIds: Array.from(next),
        syncStatus: liveReview.syncPlan?.status,
        gitHeadCommit: liveReview.syncPlan?.gitHeadCommit,
        gitChangeCount: liveReview.syncPlan?.gitChanges.length,
        qlikChangeCount: liveReview.syncPlan?.qlikChanges.length,
        conflictCount: liveReview.syncPlan?.conflicts.length,
        ...(next.size === activeReview.changes.length && !hasConflicts ? { completedAt: new Date().toISOString() } : {}),
      });
    };
    return (
      <section className="changes-page">
        <ReviewBreadcrumb appId={app.id} appName={app.name} />
        <ReviewBanner app={app} state={isIncomingGit ? 'Pull required' : `${activeReview.changes.length} change${activeReview.changes.length === 1 ? '' : 's'}`} />
        <AppSectionTabs appId={app.id} active="commit" commitCount={activeReview.changes.length} previewAppId={previewAppId} />
        <div className="review-proof-strip"><CheckCircleOutlined /><strong>{isIncomingGit ? 'Incoming Git comparison' : previewAppId ? 'Real Preview comparison' : 'Real Qlik comparison'}</strong><span>{liveReview.exportedFiles} canonical JSON files</span><span>{isIncomingGit ? 'Git updates ready to pull' : 'Qlik updates ready to push'}</span><span className="evidence-digest">Evidence {liveReview.reviewedContentDigest.slice(0, 12)}</span></div>
        <SyncPlanAlert review={liveReview} />
        {reviewComplete && <section className="commit-ready-card"><div><CheckCircleOutlined /><span><strong>Evidence approved — ready to commit</strong><small>The reviewed Qlik content and current Git state are locked by this review token.</small></span></div><label>Commit message<Input.TextArea aria-label="Commit message" value={commitMessage} maxLength={200} autoSize={{ minRows: 1, maxRows: 2 }} onChange={event => setCommitMessage(event.target.value)} /></label><Button type="primary" icon={<CloudUploadOutlined />} loading={checkingIn} onClick={() => void commitChanges()}>Commit to GitHub</Button></section>}
        {commitResult && <Alert className="live-review-context" showIcon type={commitResult.type} message={commitResult.message} />}

        <div className="change-layout is-roadmap-layout">
          <ReviewExecutionRoadmap loading={false} mode={isIncomingGit ? 'checkout' : 'commit'} exportedFiles={liveReview.exportedFiles} changeCount={activeReview.changes.length} evidenceDigest={liveReview.reviewedContentDigest} reviewComplete={isIncomingGit || reviewComplete} conflictCount={liveReview.syncPlan?.conflicts.length} targetLabel={previewAppId ? 'Preview App' : 'current Qlik App'} />

          <main className="change-review is-scrollable-review">
            <div className="change-review-scroll" tabIndex={0} aria-label="Changed object review content">
            <section className={`multi-review-summary is-${highestRisk}`}>
              <div>
                <span className="summary-eyebrow">{isIncomingGit ? 'Incoming Git review' : 'Qlik change review'}</span>
                <h2>{activeReview.changes.length} changed Qlik object{activeReview.changes.length === 1 ? '' : 's'}</h2>
                <p>{activeReview.summary}</p>
              </div>
              <div className="review-summary-metrics" aria-label="Change categories">
                <span><strong>{visualChangeCount}</strong> Visual</span>
                <span><strong>{businessLogicCount}</strong> Business logic</span>
                <span><strong>{labelChangeCount}</strong> Label</span>
                <span><strong>{layoutChangeCount}</strong> Layout</span>
              </div>
              <div className="review-gate-meter">
                <strong>{reviewedRequiredCount}/{requiredChanges.length}</strong>
                <span>{requiredChanges.length ? 'required reviews complete' : 'no required high-impact review'}</span>
                {!isIncomingGit && lowRiskChanges.length > 0 && <Button size="small" disabled={reviewedLowRiskCount === lowRiskChanges.length} onClick={reviewLowRiskChanges}>Approve low-risk changes</Button>}
              </div>
            </section>
            <div className="multi-review-workspace">
              <aside className="object-review-queue" aria-label="Changed Qlik object queue">
                <div className="queue-heading">
                  <strong>{isIncomingGit ? 'Incoming objects' : 'Changed objects'}</strong>
                  <span>Review each business-relevant change; low-risk label-only changes can be approved together.</span>
                </div>
                {orderedGroups.map(group => (
                  <div className="queue-group" key={group.category}>
                    <h3>{categoryLabels[group.category]} <span>{group.changes.length}</span></h3>
                    {group.changes.map(change => {
                      const index = activeReview.changes.findIndex(item => item.objectId === change.objectId && item.folder === change.folder);
                      const isReviewed = reviewedObjectIds.has(change.objectId);
                      return (
                        <button
                          className={`queue-change-item ${index === selectedIndex ? 'is-selected' : ''} ${isReviewed ? 'is-reviewed' : ''}`}
                          key={`${change.folder}:${change.objectId}`}
                          onClick={() => { setSelectedIndex(index); setPropertiesOpen(false); }}
                        >
                          <span className="change-object-icon">{objectIconFor(change)}</span>
                          <span>
                            <strong>{change.title ?? change.objectId}</strong>
                            <small>{change.objectType} · {capitalize(change.changeType)} · {businessImpactLabel(change)}</small>
                          </span>
                          <i className={`risk-${change.risk}`}>{isReviewed ? 'Reviewed' : capitalize(change.risk)}</i>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </aside>
              <section className="selected-change-detail">
                <AiChangeInterpretation change={selected} pair={visualPair} annotations={visualAnnotations} showAnnotations={showAnnotations} onToggleAnnotations={() => setShowAnnotations(value => !value)} visual={visualPair ? <CapabilityVisualDiff appId={reviewTargetId} beforeLabel={isIncomingGit ? 'Current Qlik App' : 'Current Git version'} afterLabel={isIncomingGit ? 'Updated Git version' : previewAppId ? 'Preview App' : 'Current Qlik App'} afterIsProperties={isIncomingGit} deferRender={isIncomingGit} pair={visualPair} annotations={visualAnnotations} showAnnotations={showAnnotations} /> : <LiveObjectEvidence change={selected} targetLabel={isIncomingGit ? 'Updated Git version' : previewAppId ? 'Preview App' : 'Current Qlik App'} />} />
                <button aria-expanded={propertiesOpen} aria-label="Property changes" className="property-toggle raw-diff-toggle" onClick={() => setPropertiesOpen(value => !value)}><span>&lt;/&gt;</span> View JSON property diff {visualPair ? `(${visualPair.propertyChanges.length})` : ''}<span>{propertiesOpen ? '−' : '+'}</span></button>
                {propertiesOpen && <div className="live-property-details">
                  {visualPair?.propertyChanges.length ? <div className="property-change-list">{visualPair.propertyChanges.map(change => <div className="property-change-row" key={change.path}><code>{change.path}</code><span className="property-before">{formatPropertyValue(change.before)}</span><strong>→</strong><span className="property-after">{formatPropertyValue(change.after)}</span></div>)}</div> : <p>No leaf-level property changes are available for this object type.</p>}
                  <dl><dt>Object ID</dt><dd><code>{selected.objectId}</code></dd><dt>Folder</dt><dd>{selected.folder}</dd><dt>Object type</dt><dd>{selected.objectType}</dd><dt>Change</dt><dd>{selected.changeType}</dd><dt>Risk</dt><dd>{selected.risk}</dd></dl>
                </div>}
              </section>
            </div>
            </div>
            {isIncomingGit ? <section className="review-progress incoming-checkout-actions"><div><strong>Incoming evidence is ready</strong><span>Review complete · Check out Git {liveReview.syncPlan?.gitHeadCommit.slice(0, 7)} from Overview</span></div><Button disabled={selectedIndex === 0} onClick={() => setSelectedIndex(value => value - 1)}>Previous change</Button><Button disabled={selectedIndex >= activeReview.changes.length - 1} onClick={() => setSelectedIndex(value => value + 1)}>Next change</Button><Link className="ant-btn ant-btn-primary" to={`/apps/${app.id}`}>Return to Overview &amp; check out</Link></section> : <section className="review-progress"><div><strong>{reviewedObjectIds.size} of {activeReview.changes.length} changes reviewed</strong><span>{reviewedRequiredCount} of {requiredChanges.length} required reviews · {reviewedLowRiskCount} of {lowRiskChanges.length} low-risk reviews</span><span className="progress-track"><span style={{ width: `${(reviewedObjectIds.size / activeReview.changes.length) * 100}%` }} /></span></div><Button icon={<CheckCircleOutlined />} disabled={reviewedObjectIds.has(selected.objectId)} onClick={markReviewed}>{reviewedObjectIds.has(selected.objectId) ? 'Reviewed' : 'Mark reviewed'}</Button><Button disabled={selectedIndex === 0} onClick={() => setSelectedIndex(value => value - 1)}>Previous change</Button><Button type="primary" disabled={selectedIndex >= activeReview.changes.length - 1} onClick={() => setSelectedIndex(value => value + 1)}>Next change</Button></section>}
          </main>
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="changes-page">
        <ReviewBreadcrumb appId={app.id} appName={app.name} />
        <ReviewBanner app={app} state="Review unavailable" />
        <AppSectionTabs appId={app.id} active="commit" previewAppId={previewAppId} />
        <Alert
          className="live-review-context"
          showIcon
          type="error"
          message="Live diff failed"
          description={loadError}
          action={<Link className="ant-btn ant-btn-primary" to={`/apps/${app.id}/commit${previewAppId ? `?previewAppId=${previewAppId}` : ''}`}>Retry live diff</Link>}
        />
      </section>
    );
  }

  return <SampleChangeReview app={app} propertiesOpen={propertiesOpen} setPropertiesOpen={setPropertiesOpen} />;
};

const ReviewBreadcrumb: React.FC<{ appId: string; appName: string }> = ({ appId, appName }) => (
  <div className="detail-breadcrumb"><Link to="/apps"><ArrowLeftOutlined /> Qlik Apps</Link><span>/</span><Link to={`/apps/${appId}`}>{appName}</Link></div>
);

const ReviewBanner: React.FC<{ app: typeof demoApps[number]; state: string }> = ({ app, state }) => (
  <section className="change-app-banner">
    <span className="app-glyph app-glyph-bars"><BarChartOutlined /></span>
    <div><h1>{app.name}</h1><div className="banner-meta"><span><GithubOutlined /> {app.repository}</span><span><BranchesOutlined /> {app.branch}</span><span className="git-state connected"><span className="status-dot" /> {state}</span></div></div>
  </section>
);

const SyncPlanAlert: React.FC<{ review: LiveReview }> = ({ review }) => {
  const plan = review.syncPlan;
  if (!plan) return null;

  if (plan.status === 'conflict') {
    return <Alert className="live-review-context" showIcon type="error" message="Merge conflict — Commit remains locked" description={`${plan.conflicts.length} Qlik object${plan.conflicts.length === 1 ? '' : 's'} changed differently in Git and this Preview. Resolve the conflicting object in Qlik or Git, then refresh the review.`} />;
  }
  if (plan.status === 'auto_merge') {
    return <Alert className="live-review-context" showIcon type="info" message="Qlik and Git both have updates" description={`Click-CI will preserve ${plan.gitChanges.length} Git update${plan.gitChanges.length === 1 ? '' : 's'} and apply ${plan.qlikChanges.length} reviewed Qlik update${plan.qlikChanges.length === 1 ? '' : 's'}.`} />;
  }
  if (plan.status === 'git_ahead') {
    return <Alert className="live-review-context" showIcon type="warning" message="Git has updates ready to pull" description="The current Qlik App does not include the latest reviewed Git content. Click-CI will not overwrite newer Git content." />;
  }
  if (plan.status === 'qlik_ahead') {
    return <div className="live-review-context sync-plan-summary"><InfoCircleOutlined /><strong>Qlik updates ready to push</strong><span>{plan.qlikChanges.length} Qlik update{plan.qlikChanges.length === 1 ? '' : 's'} ready for review</span></div>;
  }
  return <div className="live-review-context sync-plan-summary is-synced"><CheckCircleOutlined /><strong>Git and Qlik are in sync</strong><span>No Qlik push or Git pull is required.</span></div>;
};

const LiveObjectEvidence: React.FC<{ change: LiveChange; targetLabel: string }> = ({ change, targetLabel }) => (
  <section className="added-object-evidence">
    <div><span>Before · Current Git version</span><strong>{change.changeType === 'added' ? 'Not present' : change.title ?? change.objectId}</strong><p>{change.changeType === 'added' ? 'This Qlik object does not exist in Git yet.' : `${change.objectType} in the current Git version.`}</p></div>
    <span className="evidence-arrow">→</span>
    <div className="after"><span>After · {targetLabel}</span><strong>{change.changeType === 'removed' ? 'Removed' : change.title ?? change.objectId}</strong><p>{change.objectType} · {capitalize(change.changeType)}</p></div>
    <small><InfoCircleOutlined /> {change.objectType === 'barchart' && change.changeType === 'modified' ? 'This chart is eligible for the Capability API visual before/after renderer.' : change.changeType === 'added' ? 'New objects have no “Before” visualization.' : 'Open Object details for the structured property-level evidence.'}</small>
  </section>
);

const formatPropertyValue = (value: unknown) => {
  const serialized = typeof value === 'string' ? value : JSON.stringify(value);
  return serialized === undefined ? 'undefined' : serialized;
};

const capitalize = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const SampleChangeReview: React.FC<{ app: typeof demoApps[number]; propertiesOpen: boolean; setPropertiesOpen: React.Dispatch<React.SetStateAction<boolean>> }> = ({ app, propertiesOpen, setPropertiesOpen }) => (
  <section className="changes-page">
    <ReviewBreadcrumb appId={app.id} appName={app.name} />
    <ReviewBanner app={app} state="Demo scenario" />
    <AppSectionTabs appId={app.id} active="commit" commitCount={12} />
    <div className="change-layout">
      <aside className="changed-objects">
        <div className="changed-objects-heading"><h2>Changed objects (12)</h2><span><SearchOutlined /><FilterOutlined /></span></div>
        <h3>Visualizations <span>(6)</span></h3>
        {sampleChanges.slice(0, 3).map((change, index) => <SampleChangeItem key={change[0]} change={change} selected={index === 0} />)}
        <h3>Measures <span>(2)</span></h3>{sampleChanges.slice(3, 5).map(change => <SampleChangeItem key={change[0]} change={change} />)}
        <h3>Sheets <span>(2)</span></h3>{sampleChanges.slice(5, 7).map(change => <SampleChangeItem key={change[0]} change={change} />)}
        <h3>Other <span>(2)</span></h3>{sampleChanges.slice(7).map(change => <SampleChangeItem key={change[0]} change={change} />)}
      </aside>
      <main className="change-review">
        <section className="business-impact"><InfoCircleOutlined /><div><strong>Business impact</strong><p>Bar chart changed to stacked bars and Region moved from color to dimension. This may change how users compare regional contribution.</p></div></section>
        <VisualDiffCard />
        <button aria-label="Property changes (8)" className="property-toggle" onClick={() => setPropertiesOpen(value => !value)}><span>☷</span> Property changes (8)<span>›</span></button>
        {propertiesOpen && <div className="property-details"><code>qHyperCube.qMode</code><span>"S"</span><strong>→</strong><span>"K"</span><code>qHyperCube.qDimensions[0].qDef.qFieldDefs</code><span>["Region"]</span><strong>→</strong><span>["Quarter"]</span></div>}
        <button aria-label="View JSON diff" className="property-toggle"><span>&lt;/&gt;</span> View JSON diff<span>›</span></button>
        <section className="review-progress"><div><strong>1 of 12 changes reviewed</strong><span className="progress-track"><span /></span></div><Button icon={<CheckCircleOutlined />}>Mark reviewed</Button><Button>Previous change</Button><Button type="primary">Next change</Button></section>
      </main>
    </div>
  </section>
);

const SampleChangeItem: React.FC<{ change: string[]; selected?: boolean }> = ({ change, selected }) => (
  <button className={`change-item ${selected ? 'is-selected' : ''}`}>
    <span className="change-object-icon">{change[1] === 'Bar chart' ? <BarChartOutlined /> : change[1] === 'KPI' ? '12.3M' : <FileTextOutlined />}</span>
    <span><strong>{change[0]}</strong><small>{change[1]}</small></span>
    <span className="change-state">{change[2]}<small className={`risk-${change[3].toLowerCase()}`}>{change[3]} ●</small></span>
  </button>
);
